// Inline SVG icon set. Feather-derived line icons, single-color via
// `currentColor` so callers can size + colour them from CSS. No external
// dependencies — keeps the admin UI bundle small and avoids icon-font flash.
//
// Usage:
//   icon('check')              → 16px <svg>
//   icon('user', { size: 20 }) → 20px <svg>
//   icon('warn', { className: 'icon-warn' })

export type IconName =
  | 'check'
  | 'x'
  | 'warn'
  | 'list'
  | 'search'
  | 'sparkle'
  | 'ledger'
  | 'plug'
  | 'sun'
  | 'moon'
  | 'building'
  | 'coin'
  | 'ban'
  | 'user'
  | 'user-plus'
  | 'lock'
  | 'unlock'
  | 'key'
  | 'webhook'
  | 'settings'
  | 'edit'
  | 'shield'
  | 'logout';

interface IconOptions {
  size?: number;
  className?: string;
  title?: string;
}

// Path bodies for each icon. Drawn on the 24×24 viewBox used by Feather.
// stroke="currentColor" is added in `icon()` so callers can recolour via CSS.
const PATHS: Record<IconName, string> = {
  check: '<polyline points="20 6 9 17 4 12" />',
  x: '<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />',
  warn:
    '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />',
  list:
    '<line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />',
  search:
    '<circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />',
  sparkle:
    '<path d="M12 3 13.5 8.5 19 10 13.5 11.5 12 17 10.5 11.5 5 10 10.5 8.5Z" /><path d="M19 17 19.6 19 21.5 19.6 19.6 20.2 19 22 18.4 20.2 16.5 19.6 18.4 19Z" />',
  ledger:
    '<path d="M4 4h12a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2Z" /><line x1="8" y1="9" x2="14" y2="9" /><line x1="8" y1="13" x2="14" y2="13" /><line x1="8" y1="17" x2="12" y2="17" />',
  plug:
    '<path d="M9 2v6M15 2v6" /><path d="M6 8h12v3a6 6 0 0 1-12 0Z" /><path d="M12 17v5" />',
  sun:
    '<circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22" /><line x1="4.93" y1="4.93" x2="6.34" y2="6.34" /><line x1="17.66" y1="17.66" x2="19.07" y2="19.07" /><line x1="2" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22" y2="12" /><line x1="4.93" y1="19.07" x2="6.34" y2="17.66" /><line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />',
  moon: '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />',
  building:
    '<rect x="4" y="3" width="16" height="18" rx="1" /><line x1="9" y1="7" x2="9" y2="7" /><line x1="15" y1="7" x2="15" y2="7" /><line x1="9" y1="11" x2="9" y2="11" /><line x1="15" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="9" y2="15" /><line x1="15" y1="15" x2="15" y2="15" /><line x1="10" y1="21" x2="10" y2="17" /><line x1="14" y1="21" x2="14" y2="17" />',
  coin:
    '<circle cx="12" cy="12" r="9" /><path d="M14.5 9.5a2.5 2.5 0 0 0-5 0c0 1.4 1 2.1 2.5 2.5s2.5 1.1 2.5 2.5a2.5 2.5 0 0 1-5 0" /><line x1="12" y1="7" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="17" />',
  ban:
    '<circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />',
  user:
    '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />',
  'user-plus':
    '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />',
  lock:
    '<rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />',
  unlock:
    '<rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />',
  key:
    '<circle cx="7.5" cy="15.5" r="3.5" /><line x1="10" y1="13" x2="21" y2="2" /><line x1="17" y1="6" x2="20" y2="9" /><line x1="14" y1="9" x2="17" y2="12" />',
  webhook:
    '<circle cx="6" cy="18" r="3" /><circle cx="18" cy="18" r="3" /><circle cx="12" cy="5" r="3" /><path d="M9.5 11 7 17" /><path d="M14.5 11 17 17" /><path d="M9 18h6" />',
  settings:
    '<circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />',
  edit:
    '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />',
  shield:
    '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />',
  logout:
    '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />',
};

export function icon(name: IconName, opts: IconOptions = {}): string {
  const size = opts.size ?? 16;
  const cls = opts.className ? ` class="icon ${opts.className}"` : ' class="icon"';
  const title = opts.title ? `<title>${opts.title}</title>` : '';
  const body = PATHS[name];
  return `<svg${cls} width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${title}${body}</svg>`;
}
